// WebSocket client for /live/ws: JSON LiveEvent frames, "ping" keepalive every
// 30 s (the LiveFeed answers "pong" without waking up), reconnect with exponential
// backoff (1 s .. 30 s).
//
// Every connection attempt is a Worker request on the free plan, so: the backoff only
// resets after a connection has stayed up for a while (a server that accepts and then
// drops the socket can't cause a 1-per-second reconnect loop), and a hidden tab waits
// until it is shown again before reconnecting.

import type { LiveEvent } from '../src/types';

export type SocketState =
  | { kind: 'connecting'; attempt: number }
  | { kind: 'open' }
  | { kind: 'retrying'; attempt: number; inMs: number }
  | { kind: 'unsupported' };

export interface LiveSocketHandlers {
  onEvent: (ev: LiveEvent) => void;
  onState: (state: SocketState) => void;
}

const EVENT_TYPES = new Set(['status', 'match_started', 'thinking', 'message', 'stage', 'verdict', 'match_ended']);
const PING_MS = 30_000;
/** Pings in a row that may go unanswered before a silent connection counts as dead. */
const MAX_MISSED_PONGS = 2;
const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
/** A connection must stay up this long before the backoff starts over from 1 s. */
const STABLE_MS = 15_000;

export function liveSocketUrl(): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/live/ws`;
}

function parseEvent(data: unknown): LiveEvent | null {
  if (typeof data !== 'string' || data === 'pong') return null;
  try {
    const obj: unknown = JSON.parse(data);
    if (obj && typeof obj === 'object' && 'type' in obj && typeof obj.type === 'string' && EVENT_TYPES.has(obj.type)) {
      return obj as LiveEvent;
    }
  } catch {
    // Not JSON (e.g. a keepalive reply): ignore.
  }
  return null;
}

export class LiveSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private pingTimer: number | undefined;
  private retryTimer: number | undefined;
  private stopped = true;
  private openedAt = 0;
  /** Pings sent since the last frame from the server. */
  private missed = 0;
  /** A reconnect came due while the tab was hidden; it runs once the tab is shown. */
  private deferred = false;
  private readonly url: string;
  private readonly handlers: LiveSocketHandlers;

  constructor(handlers: LiveSocketHandlers, url: string = liveSocketUrl()) {
    this.handlers = handlers;
    this.url = url;
  }

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    if (typeof WebSocket === 'undefined') {
      this.handlers.onState({ kind: 'unsupported' });
      return;
    }
    document.addEventListener('visibilitychange', this.onVisibility);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.deferred = false;
    this.drop();
  }

  /** Skips any pending backoff and reconnects now (e.g. when the tab becomes visible). */
  reconnectNow(): void {
    if (this.stopped || this.ws) return;
    window.clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.deferred = false;
    this.connect();
  }

  private readonly onVisibility = (): void => {
    if (document.hidden || this.stopped) return;
    // Background tabs throttle timers, so pongs may look late: start the count again.
    this.missed = 0;
    if (!this.ws && this.deferred) this.reconnectNow();
  };

  private connect(): void {
    this.handlers.onState({ kind: 'connecting', attempt: this.attempt });
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.openedAt = Date.now();
      this.missed = 0;
      this.handlers.onState({ kind: 'open' });
      window.clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => this.keepalive(ws), PING_MS);
    };
    ws.onmessage = (msg: MessageEvent) => {
      if (this.ws !== ws) return;
      this.missed = 0;
      const ev = parseEvent(msg.data);
      if (!ev) return;
      try {
        this.handlers.onEvent(ev);
      } catch (err) {
        console.error('live: event handler failed', err);
      }
    };
    ws.onerror = () => {
      // A close event always follows; reconnecting happens there.
    };
    ws.onclose = () => {
      if (this.ws === ws) this.lost();
    };
  }

  /** Sends a ping, or gives up on a connection that stopped answering them. */
  private keepalive(ws: WebSocket): void {
    if (this.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
    if (this.missed >= MAX_MISSED_PONGS) {
      // Half-open connections (sleep, network change) can stay "open" for minutes.
      this.lost();
      return;
    }
    this.missed++;
    try {
      ws.send('ping');
    } catch {
      // Closing; onclose follows.
    }
  }

  /** The current connection is gone: drop it and schedule the next attempt. */
  private lost(): void {
    if (this.openedAt && Date.now() - this.openedAt >= STABLE_MS) this.attempt = 0;
    this.drop();
    if (!this.stopped) this.scheduleRetry();
  }

  private drop(): void {
    window.clearInterval(this.pingTimer);
    this.pingTimer = undefined;
    this.openedAt = 0;
    const ws = this.ws;
    this.ws = null;
    if (!ws) return;
    ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
    try {
      ws.close(1000, 'bye');
    } catch {
      // already closing
    }
  }

  private scheduleRetry(): void {
    const base = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** Math.min(this.attempt, 10));
    // Up to 20% jitter so many tabs don't reconnect in lockstep after a deploy.
    const inMs = Math.min(MAX_BACKOFF_MS, Math.round(base * (1 + Math.random() * 0.2)));
    this.attempt++;
    this.handlers.onState({ kind: 'retrying', attempt: this.attempt, inMs });
    window.clearTimeout(this.retryTimer);
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined;
      if (this.stopped) return;
      if (document.hidden) {
        this.deferred = true;
        return;
      }
      this.connect();
    }, inMs);
  }
}
